import { Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import { useEffect } from "react";

import ForgotPassword from "./component/ForgotPassword";
import ProtectedRoute from "./middleware/ProtectedRoute";
import Login from "./component/Login";

import SuperadminDashboard from "./component/Superadmin/SuperadminDashboard";
import AllOrgs from "./component/Superadmin/AllOrgs";
import CreateOrg from "./component/Superadmin/CreateOrg";
import EditOrg from "./component/Superadmin/EditOrg";
import EditAdmin from "./component/Superadmin/EditAdmin";

// Admin screens shared by every module (base path is resolved from the URL)
import AdminDashboard from "./component/Admin/AdminDashboard";
import OrgDetails from "./component/Admin/OrgDetails";
import AllOrgsUsers from "./component/Admin/AllOrgUsers";
import CreateUser from "./component/Admin/CreateUser";
import UploadBOM from "./component/Admin/UploadBOM";
import UploadPartMaster from "./component/Admin/UploadPartMaster";

import UserDashboard from "./component/Users/UserDashboard";
import DashboardRedirect from "./component/Users/DashboardRedirect";
import SalesReport from "./component/Users/Planner/SalesReport";
import Shortages from "./component/Users/Shortages";
import Materials from "./component/Users/Materials";
import Requisitions from "./component/Users/Requisitions";
import PurchaseOrders from "./component/Users/PurchaseOrders";
import UploadReports from "./component/Users/UploadReports";
import ProductionReport from "./component/Users/ProductionReport";
import Snapshots from "./component/Users/Snapshots";
import MaterialDetail from "./component/Users/MaterialDetail";
import Reserves from "./component/Users/Planner/Reserves";
import AllocationDetails from "./component/Users/Planner/AllocationDetails";
import Allotments from "./component/Users/Planner/Allotments";
import ModuleLanding from "./component/Users/ModuleLanding";
import {
  ALL_USER_ROLE_VALUES,
  MODULES,
  getModuleRoles,
} from "./utils/moduleAccess";

// Approval Management (eApproval) module
import EAAdminControl from "./component/ApprovalManagement/Admin/AdminControl";
import EAVendorList from "./component/ApprovalManagement/Admin/VendorList";
import EAAddVendor from "./component/ApprovalManagement/Admin/AddVendor";
import EAPlantDetails from "./component/ApprovalManagement/Admin/PlantDetails";
import EAAddPlant from "./component/ApprovalManagement/Admin/AddPlant";
import EAUserDashboard from "./component/ApprovalManagement/Users/UserDashboard";
import EADashboard from "./component/ApprovalManagement/Users/Dashboard";
import EASuperuserDashboard from "./component/ApprovalManagement/Users/Others/SuperuserDashboard";
import EAViewForm from "./component/ApprovalManagement/Users/ViewForm";
import EASRACreation from "./component/ApprovalManagement/Users/Buyer/SRACreation/SRACreation";
import EASRAUpdate from "./component/ApprovalManagement/Users/Buyer/SRAUpdate/SRAUpdate";
import EAVendorBalanceCreation from "./component/ApprovalManagement/Users/Buyer/VendorBalanceCreation/VendorBalanceCreation";
import EAVendorBalanceUpdate from "./component/ApprovalManagement/Users/Buyer/VendorBalanceUpdate/VendorBalanceUpdate";
import EAInvoiceApprovalCreation from "./component/ApprovalManagement/Users/Buyer/InvoiceApprovalCreation/InvoiceApprovalCreation";
import EAInvoiceApprovalUpdate from "./component/ApprovalManagement/Users/Buyer/InvoiceApprovalUpdate/InvoiceApprovalUpdate";
import EACustomerApprovalCreation from "./component/ApprovalManagement/Users/Buyer/CustomerApprovalCreation/CustomerApprovalCreation";
import EACustomerApprovalUpdate from "./component/ApprovalManagement/Users/Buyer/CustomerApprovalUpdate/CustomerApprovalUpdate";
import EATradeMarketingCreation from "./component/ApprovalManagement/Users/Buyer/TradeMarketingCreation/TradeMarketingCreation";
import EATradeMarketingUpdate from "./component/ApprovalManagement/Users/Buyer/TradeMarketingUpdate/TradeMarketingUpdate";
import EACustomerBalanceCreation from "./component/ApprovalManagement/Users/Buyer/CustomerBalanceCreation/CustomerBalanceCreation";
import EACustomerBalanceUpdate from "./component/ApprovalManagement/Users/Buyer/CustomerBalanceUpdate/CustomerBalanceUpdate";
import EAAdvanceGivenCreation from "./component/ApprovalManagement/Users/Buyer/AdvanceGivenCreation/AdvanceGivenCreation";
import EAAdvanceGivenUpdate from "./component/ApprovalManagement/Users/Buyer/AdvanceGivenUpdate/AdvanceGivenUpdate";
import EADepositGivenCreation from "./component/ApprovalManagement/Users/Buyer/DepositGivenCreation/DepositGivenCreation";
import EADepositGivenUpdate from "./component/ApprovalManagement/Users/Buyer/DepositGivenUpdate/DepositGivenUpdate";
import EAInventoryCreation from "./component/ApprovalManagement/Users/Buyer/InventoryCreation/InventoryCreation";
import EAInventoryUpdate from "./component/ApprovalManagement/Users/Buyer/InventoryUpdate/InventoryUpdate";
import EAOtherCreation from "./component/ApprovalManagement/Users/Buyer/OtherFormCreation/OtherCreation";
import EAOtherUpdate from "./component/ApprovalManagement/Users/Buyer/OtherFormUpdate/OtherUpdate";
import EACustomerDepositCreation from "./component/ApprovalManagement/Users/Buyer/CustomerDepositCreation/CustomerDepositCreation";
import EACustomerDepositUpdate from "./component/ApprovalManagement/Users/Buyer/CustomerDepositUpdate/CustomerDepositUpdate";
import EATenderDepositCreation from "./component/ApprovalManagement/Users/Buyer/TenderDepositCreation/TenderDepositCreation";
import EATenderDepositUpdate from "./component/ApprovalManagement/Users/Buyer/TenderDepositUpdate/TenderDepositUpdate";
import EAVendorAdvanceCreation from "./component/ApprovalManagement/Users/Buyer/VendorAdvanceCreation/VendorAdvanceCreation";
import EAVendorAdvanceUpdate from "./component/ApprovalManagement/Users/Buyer/VendorAdvanceUpdate/VendorAdvanceUpdate";
import EAVendorApprovalCreation from "./component/ApprovalManagement/Users/Buyer/VendorApprovalCreation/VendorApprovalCreation";
import EAVendorApprovalUpdate from "./component/ApprovalManagement/Users/Buyer/VendorApprovalUpdate/VendorApprovalUpdate";

// New Product Development (NPD) module
import NpdApp from "./component/NPD/NpdApp";

const APPROVAL_USER_ROLES = getModuleRoles(MODULES.APPROVAL);
const NPD_USER_ROLES = getModuleRoles(MODULES.NPD);

// Create (with optional draft) and edit routes for each eApproval form type
const approvalForms = [
  { slug: "service-order", Create: EASRACreation, Update: EASRAUpdate },
  { slug: "vendor-balance", Create: EAVendorBalanceCreation, Update: EAVendorBalanceUpdate },
  { slug: "invoice-approval", Create: EAInvoiceApprovalCreation, Update: EAInvoiceApprovalUpdate },
  { slug: "customer-approval", Create: EACustomerApprovalCreation, Update: EACustomerApprovalUpdate },
  { slug: "trade", Create: EATradeMarketingCreation, Update: EATradeMarketingUpdate },
  { slug: "customer-balance", Create: EACustomerBalanceCreation, Update: EACustomerBalanceUpdate },
  { slug: "advance-given", Create: EAAdvanceGivenCreation, Update: EAAdvanceGivenUpdate },
  { slug: "deposit-given", Create: EADepositGivenCreation, Update: EADepositGivenUpdate },
  { slug: "inventory", Create: EAInventoryCreation, Update: EAInventoryUpdate },
  { slug: "other", Create: EAOtherCreation, Update: EAOtherUpdate },
  { slug: "customer-deposit", Create: EACustomerDepositCreation, Update: EACustomerDepositUpdate },
  { slug: "tender-deposit", Create: EATenderDepositCreation, Update: EATenderDepositUpdate },
  { slug: "vendor-advance", Create: EAVendorAdvanceCreation, Update: EAVendorAdvanceUpdate },
  { slug: "vendor-approval", Create: EAVendorApprovalCreation, Update: EAVendorApprovalUpdate },
];

function App() {
  useEffect(() => {
    const wheelHandler = (e) => {
      if (document.activeElement.matches("input[type=number]")) {
        e.preventDefault();
      }
    };

    const keyHandler = (e) => {
      if (
        document.activeElement.matches("input[type=number]") &&
        (e.key === "ArrowUp" || e.key === "ArrowDown")
      ) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", wheelHandler, {
      passive: false,
    });
    window.addEventListener("keydown", keyHandler);

    return () => {
      window.removeEventListener("wheel", wheelHandler);
      window.removeEventListener("keydown", keyHandler);
    };
  }, []);

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/user-onboarding" element={<ForgotPassword />} />

        {/* Module selection for all roles except SUPERADMIN */}
        <Route
          element={
            <ProtectedRoute
              allowedRoles={["ADMIN", ...ALL_USER_ROLE_VALUES]}
            />
          }
        >
          <Route path="/modules" element={<ModuleLanding />} />
        </Route>

        {/* Approval Management (eApproval) - ADMIN */}
        <Route element={<ProtectedRoute allowedRoles={["ADMIN"]} />}>
          <Route path="/approval/admin" element={<AdminDashboard />}>
            <Route index element={<AllOrgsUsers />} />
            <Route path="org-details" element={<OrgDetails />} />
            <Route path="create-user" element={<CreateUser />} />
            <Route path="requests" element={<EASuperuserDashboard />} />
            <Route path="controls" element={<EAAdminControl />} />
            <Route path="vendors" element={<EAVendorList />} />
            <Route path="add-vendor" element={<EAAddVendor />} />
            <Route path="add-vendor/:vendorCode" element={<EAAddVendor />} />
            <Route path="plants" element={<EAPlantDetails />} />
            <Route path="add-plant" element={<EAAddPlant />} />
            <Route path="add-plant/:plantId" element={<EAAddPlant />} />
            <Route path=":orgUserId" element={<CreateUser />} />
          </Route>
        </Route>

        {/* Approval Management (eApproval) - users */}
        <Route element={<ProtectedRoute allowedRoles={APPROVAL_USER_ROLES} />}>
          <Route path="/approval/dashboard" element={<EAUserDashboard />}>
            <Route index element={<EADashboard />} />
            <Route path="vendors" element={<EAVendorList />} />
            <Route path="add-vendor" element={<EAAddVendor />} />
            {approvalForms.map(({ slug, Create, Update }) => [
              <Route key={`create-${slug}`} path={`create-${slug}`} element={<Create />} />,
              <Route
                key={`draft-${slug}`}
                path={`create-${slug}/draft/:draftSraId`}
                element={<Create />}
              />,
              <Route key={`edit-${slug}`} path={`edit-${slug}/:sraId`} element={<Update />} />,
            ])}
            <Route path=":sraId" element={<EAViewForm />} />
          </Route>
        </Route>

        {/* New Product Development (NPD) - ADMIN and users each get their own dashboard */}
        <Route element={<ProtectedRoute allowedRoles={["ADMIN"]} />}>
          <Route path="/npd/admin/*" element={<NpdApp admin />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={NPD_USER_ROLES} />}>
          <Route path="/npd/dashboard/*" element={<NpdApp admin={false} />} />
        </Route>

        {/* SUPERADMIN */}
        <Route element={<ProtectedRoute allowedRoles={["SUPERADMIN"]} />}>
          <Route path="/superadmin-dashboard" element={<SuperadminDashboard />}>
            <Route index element={<AllOrgs />} />
            <Route path="create-org" element={<CreateOrg />} />
            <Route path=":orgId" element={<EditOrg />} />
            <Route path=":orgId/admin" element={<EditAdmin />} />
          </Route>
        </Route>

        {/* ADMIN */}
        <Route element={<ProtectedRoute allowedRoles={["ADMIN"]} />}>
          <Route path="/admin" element={<AdminDashboard />}>
            <Route index element={<AllOrgsUsers />} />
            <Route path="org-details" element={<OrgDetails />} />
            <Route path="create-user" element={<CreateUser />} />
            <Route path=":orgUserId" element={<CreateUser />} />
            <Route path="bom" element={<UploadBOM />} />
            <Route path="part-master" element={<UploadPartMaster />} />
          </Route>
        </Route>

        {/* Material Tracker users */}
        <Route
          element={
            <ProtectedRoute
              allowedRoles={["BUYER", "PURCHASE_HEAD", "PLANNER", "PLANT_HEAD"]}
            />
          }
        >
          <Route path="/" element={<UserDashboard />}>
            <Route path="dashboard" element={<DashboardRedirect />} />
            <Route path="sales-reports" element={<SalesReport />} />
            <Route path="shortages" element={<Shortages />} />
            <Route path="materials" element={<Materials />} />
            <Route path="requisitions" element={<Requisitions />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route
              path="upload"
              element={<UploadReports reportType="combined" />}
            />
            <Route
              path="upload-open-pr"
              element={<UploadReports reportType="openPR" />}
            />
            <Route
              path="upload-open-po"
              element={<UploadReports reportType="openPO" />}
            />
            <Route
              path="upload-delivery"
              element={<UploadReports reportType="delivery" />}
            />
            <Route path="production-report" element={<ProductionReport />} />
            <Route path="snapshots" element={<Snapshots />} />
            <Route path="shortages/:key" element={<MaterialDetail />} />
            <Route path="reserves" element={<Reserves />} />
            <Route
              path="reserves/:allocationId"
              element={<AllocationDetails />}
            />
            <Route path="allotments" element={<Allotments />} />
            <Route
              path="allotments/:allocationId"
              element={<AllocationDetails />}
            />
          </Route>
        </Route>

        {/* Unknown paths */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
